/**
 * Configuración global para drawers
 *
 * Este archivo centraliza todos los estilos y configuraciones de drawers
 * para asegurar consistencia en toda la aplicación.
 */

import { colors } from './colors';
import { spacing } from './spacing';

export interface DrawerSize {
  width: {
    xs: string;
    sm: string;
    md?: string;
    lg?: string;
  };
  maxWidth: string;
}

export interface DrawerHeaderStyle {
  padding: string;
  backgroundColor: string;
  borderBottom: string;
  typography: {
    variant: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    fontSize: string;
    fontWeight: number;
  };
}

export interface DrawerContentStyle {
  padding: string;
  backgroundColor: string;
}

export interface DrawerFooterStyle {
  padding: string;
  backgroundColor: string;
  borderTop: string;
  gap: string;
}

export const drawerConfig = {
  // Tamaños estándar de drawers
  sizes: {
    sm: {
      width: {
        xs: '100%',
        sm: '400px',
      },
      maxWidth: '400px',
    } as DrawerSize,
    md: {
      width: {
        xs: '100%',
        sm: '600px',
      },
      maxWidth: '600px',
    } as DrawerSize,
    lg: {
      width: {
        xs: '100%',
        sm: '800px',
      },
      maxWidth: '800px',
    } as DrawerSize,
    xl: {
      width: {
        xs: '100%',
        sm: '50%',
      },
      maxWidth: '900px',
    } as DrawerSize,
  },

  // Estilos de header
  headers: {
    default: {
      padding: `${spacing.md * 2.5}px ${spacing.lg}px`,
      backgroundColor: colors.background.default,
      borderBottom: `1px solid ${colors.grey[200]}`,
      typography: {
        variant: 'h6' as const,
        fontSize: '1.25rem',
        fontWeight: 600,
      },
    } as DrawerHeaderStyle,
  },

  // Estilos de contenido
  content: {
    default: {
      padding: spacing.lg + 'px',
      backgroundColor: colors.grey[50],
    } as DrawerContentStyle,
    compact: {
      padding: spacing.md + 'px',
      backgroundColor: colors.background.default,
    } as DrawerContentStyle,
    spacious: {
      padding: spacing.xl + 'px',
      backgroundColor: colors.grey[50],
    } as DrawerContentStyle,
  },

  // Estilos de footer
  footer: {
    default: {
      padding: `${spacing.md}px ${spacing.lg}px`,
      backgroundColor: colors.background.default,
      borderTop: `1px solid ${colors.grey[200]}`,
      gap: spacing.sm + 'px',
    } as DrawerFooterStyle,
    minimal: {
      padding: `${spacing.sm}px ${spacing.md}px`,
      backgroundColor: 'transparent',
      borderTop: 'none',
      gap: spacing.xs + 'px',
    } as DrawerFooterStyle,
  },

  // Sombras
  shadows: {
    small: '0 2px 8px rgba(0, 0, 0, 0.1)',
    medium: '0 4px 16px rgba(0, 0, 0, 0.12)',
    large: '0 20px 60px rgba(0, 0, 0, 0.18)',
  },
} as const;

export type DrawerSizeKey = keyof typeof drawerConfig.sizes;
export type DrawerHeaderStyleKey = keyof typeof drawerConfig.headers;
export type DrawerContentStyleKey = keyof typeof drawerConfig.content;
export type DrawerFooterStyleKey = keyof typeof drawerConfig.footer;
